/**
 * The lens detail page — every attribute, cited or not (REQ-WEB-024).
 *
 * "All the detail" is larger than it sounds. Provenance attaches to each
 * *value*, so a fully-cited lens carries a source, a retrieval date, possibly
 * competing values and a supersession history for each of ~24 attributes.
 * Rendering all of that inline gives a page nobody can scan.
 *
 * So the value and its unit are always visible, and the citation and history
 * are one tap away — which is exactly what `/lenses/{slug}/values/{attribute}`
 * already returns, and it is only fetched when someone asks for it.
 *
 * Attributes with nothing cited are shown, not hidden. At 12.9 % complete this
 * page is mostly dashes, and that is the honest picture — it is also the only
 * place a contributor can see what specifically needs citing (REQ-WEB-015).
 */
import type { ComponentChildren } from "preact";
import { useEffect, useState } from "preact/hooks";

import { AgreementMark } from "./agreement";
import type { Attribute, MountList } from "./api";
import { api, ApiProblem } from "./api";
import {
  citationHref, citedValueText, formatMoney, formatNumber, formatPartialDate, NOT_APPLICABLE, UNKNOWN,
  withUnitCode,
} from "./format";
import { setPageMeta, lensTitle } from "./meta";
import { byGroupThenName } from "./query";
import {
  isOwnSource, LOAD_FAILED, OWN_SOURCE_LABEL, sourceKindLabel, useFocusSource,
} from "./provenance";
import { LegendNote } from "./agreement";

interface DetailAttribute {
  key: string;
  label: string;
  description: string | null;
  datatype: string;
  is_core: boolean;
  is_expected: boolean;
  prominence: number;
  /** Shown in the row of this other attribute (#214, REQ-WEB-024). */
  shown_with?: string | null;
  state: "known" | "unknown" | "not_applicable";
  value?: number | string | boolean | null;
  unit?: string | null;
  source_id?: number | null;
  competing_values?: number;
  distinct_values?: number;
  distinct_publishers?: number;
  values_agree?: boolean;
  submitted_value?: number | string | boolean | null;
  submitted_unit?: string | null;
  /** Worked out for a chosen camera format, not stored: no citation (REQ-WEB-035). */
  computed?: boolean;
  for_format?: string;
}

/** Which format a lens's category refers to (REQ-WEB-035, D42/D43). */
interface CategoryBasis {
  /** The registry key of the attribute this describes, named by the API. */
  attribute: string;
  /** "chosen": the camera's format. "own": the lens's own format, stored or because the camera crops to it. */
  kind: "chosen" | "own";
  format: string | null;
  format_name: string | null;
  crop_factor: number | null;
  chosen_format?: string | null;
  chosen_format_name?: string | null;
}

interface DetailResponse {
  slug: string;
  product_code: string;
  model_name: string;
  manufacturer: { slug: string; name: string };
  mount: { slug: string; name: string };
  coverage: string | null;
  lens_type: string | null;
  announced_on: string | null;
  discontinued_on: string | null;
  groups: { key: string; attributes: DetailAttribute[] }[];
  /** REQ-WEB-045: written by lens-specs; null when there is none or it was withdrawn. */
  description?: { text: string; value_id: number; source_id: number; written_from: number[] } | null;
  category_basis?: CategoryBasis;
  completeness: { expected_applicable: number; expected_known: number; percent: number | null };
}

interface HistoryValue {
  id: number;
  value: number | string | boolean | null;
  unit: string | null;
  submitted_value: number | string | boolean | null;
  submitted_unit: string | null;
  status: string;
  is_preferred: boolean;
  /** A source's statement that the attribute does not apply to this lens (D45). */
  not_applicable?: boolean;
  superseded_by: number | null;
  note: string | null;
  recorded_at: string;
  source: {
    id: number;
    url: string;
    title: string | null;
    publisher: string | null;
    source_type: string;
    retrieved_at: string | null;
  } | null;
}

interface HistoryResponse {
  values: HistoryValue[];
  accepted_count: number;
  has_disagreement: boolean;
}

/** Present a detail value using the registry's display unit, as the list does. */
function present(a: DetailAttribute, reg: Attribute | undefined): string {
  if (a.state !== "known" || a.value === null || a.value === undefined) return UNKNOWN;
  if (typeof a.value === "boolean") return a.value ? "Yes" : "No";
  // A release date at the precision recorded, never completed (REQ-DM-028).
  if (a.datatype === "date" && typeof a.value === "string") return formatPartialDate(a.value);
  if (typeof a.value === "string") {
    const opt = reg?.options.find((o) => o.value === a.value);
    return opt ? opt.label : a.value;
  }
  // A price is shown in the currency it was published in, never converted (REQ-DM-031).
  if (reg?.unit_family === "currency" && a.unit) return formatMoney(a.value, a.unit);
  const factor = reg?.display_factor ?? null;
  const shown = factor && factor !== 0 ? a.value / factor : a.value;
  const text = formatNumber(shown, reg?.display_step ?? null);
  const unit = reg?.display_unit ?? a.unit ?? "";
  if (!unit || reg?.unit_family === "none") return text;
  // The shared rule, so a count reads "12", not "12 n" (#213).
  return withUnitCode(unit, text);
}

// ---------------------------------------------------------------------------

/** The citation and history for one attribute. Fetched only when opened. */
function Provenance(props: { slug: string; attrKey: string; reg?: Attribute | undefined }) {
  const [data, setData] = useState<HistoryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ac = new AbortController();
    fetch(`/api/v1/lenses/${props.slug}/values/${props.attrKey}`, { signal: ac.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(LOAD_FAILED))))
      .then((d: HistoryResponse) => setData(d))
      .catch(() => {
        if (!ac.signal.aborted) setError(LOAD_FAILED);
      });
    return () => ac.abort();
  }, [props.slug, props.attrKey]);

  if (error) return <p class="prov-error">{error}</p>;
  if (!data) return <p class="prov-loading">Loading citation…</p>;
  if (data.values.length === 0) {
    return (
      <p class="prov-empty">
        Nobody has cited this yet.
      </p>
    );
  }

  return (
    <div class="prov">
      {data.has_disagreement ? (
        <p class="prov-conflict">
          Sources disagree. Both values are kept; the preferred one is marked.
        </p>
      ) : null}
      <ol class="prov-list">
        {data.values.map((v) => (
          <li key={v.id} class={`prov-item prov-${v.status}`}>
            <div class="prov-value">
              <span class="prov-number">
                {/* A boolean renders as nothing in JSX, so every yes/no citation
                    showed no value at all (#214). The value is worded as its row
                    is: "7", "f/16", a choice's label, not the stored code (#213). */}
                {v.not_applicable
                  ? NOT_APPLICABLE
                  : citedValueText(props.reg, v.submitted_value ?? v.value, v.submitted_unit ?? v.unit)}
              </span>
              {v.is_preferred ? <span class="prov-badge">preferred</span> : null}
              {v.status !== "accepted" ? (
                <span class="prov-badge prov-badge-muted">{v.status}</span>
              ) : null}
            </div>
            {v.source ? (
              <p class="prov-source">
                <a href={citationHref(v.source.url)} target="_blank" rel="noopener noreferrer">
                  {isOwnSource(v.source)
                    ? OWN_SOURCE_LABEL
                    : (v.source.publisher ?? v.source.title ?? v.source.url)}
                </a>
                <span class="prov-meta">
                  {isOwnSource(v.source) ? "" : sourceKindLabel(v.source)}
                  {v.source.retrieved_at
                    ? `${isOwnSource(v.source) ? "" : " · "}retrieved ${v.source.retrieved_at}`
                    : ""}
                </span>
              </p>
            ) : (
              <p class="prov-source prov-meta">No source recorded.</p>
            )}
            {/* REQ-WEB-005 (D72): what the value is, beside its source, e.g. a
                size the maker states for another mount. */}
            {v.note ? <p class="prov-note">{v.note}</p> : null}
          </li>
        ))}
      </ol>
    </div>
  );
}

// ---------------------------------------------------------------------------

function Row(props: {
  slug: string;
  a: DetailAttribute;
  reg: Attribute | undefined;
  /** A second attribute shown in this row: "Yes · 5 stops" (#214). */
  companion?: DetailAttribute | undefined;
  registry?: Map<string, Attribute>;
  /** Under the value: which format this category refers to (REQ-WEB-035). */
  basis?: ComponentChildren;
}) {
  const { a, reg, companion } = props;
  const [open, setOpen] = useState(false);

  /**
   * Three states, not two. "Unknown" means nobody has cited it and someone
   * should; "not applicable" means the value cannot exist — a prime's
   * tele aperture is definitionally its wide one. Conflating them badges a
   * lens with work that can never be done, which is the exact failure the
   * REQ-WEB-007 amendment was written to prevent.
   */
  const notApplicable = a.state === "not_applicable";
  // "Not applicable" two ways (REQ-DM-008, D45): a rule says so, and there is
  // nothing to cite; or a source said so for this lens, and it has a citation
  // like any value.
  const citedNa = notApplicable && a.source_id != null;
  const own = notApplicable ? NOT_APPLICABLE : present(a, reg);
  const extra = companion && companion.state === "known"
    ? present(companion, props.registry?.get(companion.key)) : null;
  // One fact, one row: the companion's value joins this one, and an unknown
  // part is left out rather than shown as "— · 5 stops".
  const text = extra ? (own === UNKNOWN ? extra : `${own} · ${extra}`) : own;
  const unknown = a.state === "unknown" && !extra;
  // REQ-WEB-005 (web-050 C7): focusing the row names its source, as the list's
  // cells do. Nothing to cite when the spec cannot apply.
  const focus = useFocusSource(a.source_id ?? null, unknown);

  return (
    <div class={`drow${unknown ? " drow-unknown" : ""}${notApplicable ? " drow-na" : ""}`}>
      <button
        type="button"
        class="drow-main"
        aria-expanded={open}
        {...(notApplicable && !citedNa ? {} : focus.focusProps)}
        onClick={() => setOpen(!open)}
      >
        <span class="drow-label">
          {a.label}
          {a.is_expected && unknown ? (
            <span class="drow-gap">missing</span>
          ) : null}
        </span>
        <span class="drow-value">{text}</span>
        {/* The same component the table uses, so the two surfaces cannot drift
            apart again (REQ-WEB-006, REQ-API-020). */}
        <AgreementMark
          competing={a.competing_values}
          distinct={a.distinct_values}
          publishers={a.distinct_publishers}
          agree={a.values_agree}
        />
        <span class="drow-chevron" aria-hidden="true" />
      </button>
      {notApplicable && !citedNa ? null : focus.captionEl}
      {props.basis ? <p class="drow-basis" data-testid="category-basis">{props.basis}</p> : null}
      {open ? (
        a.computed ? (
          <p class="prov-empty">
            This category is worked out by lens-specs for the camera you chose, not stated by
            a source. The rule is on the <a href="/methodology#lens-category">Methodology page</a>.
          </p>
        ) : notApplicable && !citedNa ? (
          <p class="prov-empty">
            This spec cannot apply to this lens, so there is nothing to cite. It is not
            counted against the lens's completeness.
          </p>
        ) : (
          <>
            {citedNa ? (
              <p class="prov-na">
                A source says this spec does not apply to this lens. It is not counted
                against the lens's completeness.
              </p>
            ) : null}
            {companion ? <p class="drow-part">{a.label}</p> : null}
            <Provenance slug={props.slug} attrKey={a.key} reg={reg} />
            {companion ? (
              <>
                <p class="drow-part">{companion.label}</p>
                <Provenance slug={props.slug} attrKey={companion.key}
                            reg={props.registry?.get(companion.key)} />
              </>
            ) : null}
          </>
        )
      ) : null}
    </div>
  );
}

const titleCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const humanise = (s: string | null) => (s ? titleCase(s.replace(/_/g, " ")) : UNKNOWN);

/**
 * The sensor size a lens covers, by the name the site's own list of sizes gives
 * it ("APS-C"). Title-casing the stored code printed "Aps c" (#213). Until that
 * list has loaded there is no honest name to print, so the cell shows a dash.
 */
function coverageName(slug: string | null, coverages: MountList["coverages"] | undefined): string {
  if (!slug) return UNKNOWN;
  return coverages?.find((c) => c.slug === slug)?.name ?? UNKNOWN;
}

/** The attribute shown in `key`'s row, if the lens has one (#214). */
function companionOf(groups: DetailResponse["groups"], key: string): DetailAttribute | undefined {
  return groups.flatMap((g) => g.attributes).find((a) => a.shown_with === key);
}

/**
 * The detail page in the filter picker's order (REQ-WEB-024 as amended by D26,
 * #214): key specs first, then the less common ones, each grouped and ordered
 * exactly as the picker groups them, by the picker's own helper. Owner: "We
 * should match order between detail pages and filter order." One helper, so
 * the two cannot drift.
 */
function pickerOrder(
  groups: DetailResponse["groups"],
  registry: Map<string, Attribute>,
): { key: string; title: string | null; groups: [string, DetailAttribute[]][] }[] {
  const every = groups.flatMap((g) => g.attributes.map((a) => ({ a, group: g.key })));
  // A companion (the registry's shown_with) is shown in its primary's row, not as a
  // row of its own, when the primary is on the page (REQ-WEB-024 as amended
  // by D26: "no fact appears as two rows").
  const onPage = new Set(every.map(({ a }) => a.key));
  const all = every.filter(({ a }) => !(a.shown_with && onPage.has(a.shown_with)));
  const tier = (keep: (p: number) => boolean) => {
    const picked = all.filter(({ a }) => keep(a.prominence));
    const byKey = new Map(picked.map(({ a }) => [a.key, a]));
    // byGroupThenName reads group and short label, from the registry where the
    // attribute is in it, else from what the detail response says.
    const asAttr = picked.map(({ a, group }) =>
      registry.get(a.key) ?? ({ key: a.key, label: a.label, short_label: null, group } as unknown as Attribute));
    return byGroupThenName(asAttr).map(
      ([g, attrs]) => [g, attrs.map((x) => byKey.get(x.key)!)] as [string, DetailAttribute[]]);
  };
  return [
    { key: "core", title: null, groups: tier((p) => p <= 1) },
    { key: "more", title: "Less common specs", groups: tier((p) => p > 1) },
  ].filter((t) => t.groups.length > 0);
}

/** One sentence saying which format the category refers to. */
function categoryBasisNote(b: CategoryBasis | undefined, mountName: string): ComponentChildren {
  if (!b) return null;
  const rule = <a href="/methodology#lens-category">Methodology</a>;
  if (b.kind === "chosen") {
    // One line, no arithmetic (#213, owner 2026-10-03); the rule is a tap away.
    return <>Worked out for {mountName} · {b.format_name} ({rule}).</>;
  }
  if (b.crop_factor !== null && b.chosen_format_name) {
    return (
      <>
        For the lens's own format, {b.format_name}: focal length × {b.crop_factor}, because a{" "}
        {b.chosen_format_name} camera crops to it ({rule}).
      </>
    );
  }
  return (
    <>
      For the lens's own format, {b.format_name}.
      {b.chosen_format_name
        ? <> No crop factor is recorded for {mountName} · {b.chosen_format_name}, so it is not
            judged for that camera.</>
        : null}
    </>
  );
}

export function LensDetail(props: {
  slug: string;
  registry: Map<string, Attribute>;
  onBack: () => void;
  /** The camera's sensor format, from the URL, so the category is judged on it (REQ-WEB-035). */
  format?: string | null | undefined;
  /** For the sensor size's name: "APS-C", not a title-cased slug (#213). */
  coverages?: MountList["coverages"] | undefined;
}) {
  const [lens, setLens] = useState<DetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ac = new AbortController();
    setLens(null);
    setError(null);
    scrollTo(0, 0);
    fetch(`/api/v1/lenses/${props.slug}${props.format ? `?format=${encodeURIComponent(props.format)}` : ""}`,
          { signal: ac.signal })
      .then((r) =>
        r.ok
          ? r.json()
          : r.status === 404
            ? Promise.reject(new Error("No lens with that name."))
            : Promise.reject(new Error(LOAD_FAILED)),
      )
      .then((d: DetailResponse) => {
        setLens(d);
        setPageMeta(lensTitle(d.manufacturer.name, d.model_name, d.mount.name));
      })
      .catch((e: unknown) => {
        if (!ac.signal.aborted) setError(e instanceof Error ? e.message : LOAD_FAILED);
      });
    return () => ac.abort();
  }, [props.slug, props.format]);

  if (error) {
    return (
      <main class="shell">
        <button type="button" class="backlink" onClick={props.onBack}>
          ← Back to the list
        </button>
        <div class="empty">
          <p class="empty-title">{error}</p>
          <p class="empty-body">
            The link may be out of date, or the lens may have been renamed.
          </p>
        </div>
      </main>
    );
  }

  if (!lens) {
    return (
      <main class="shell">
        <p class="loading" role="status">
          Loading…
        </p>
      </main>
    );
  }

  const c = lens.completeness;
  const gaps = lens.groups
    .flatMap((g) => g.attributes)
    .filter((a) => a.is_expected && a.state === "unknown");

  return (
    <main class="shell detail">
      <button type="button" class="backlink" onClick={props.onBack}>
        ← Back to the list
      </button>

      <header class="dhead">
        <p class="dhead-brand">{lens.manufacturer.name}</p>
        <h1 class="dhead-name">{lens.model_name}</h1>
      </header>

      <dl class="dfacts">
        <div>
          <dt>Mount</dt>
          <dd>{lens.mount.name}</dd>
        </div>
        <div>
          <dt>Sensor size covered</dt>
          <dd>{coverageName(lens.coverage, props.coverages)}</dd>
        </div>
        <div>
          <dt>Prime or zoom</dt>
          <dd>{humanise(lens.lens_type)}</dd>
        </div>
        <div>
          <dt>Announced</dt>
          <dd>{lens.announced_on ?? UNKNOWN}</dd>
        </div>
        {lens.discontinued_on ? (
          <div>
            <dt>Discontinued</dt>
            <dd>{lens.discontinued_on}</dd>
          </div>
        ) : null}
      </dl>

      {lens.description?.text ? (
        <section class="ddesc" aria-label="Description">
          <p class="ddesc-text">{lens.description.text}</p>
          <p class="ddesc-label">Written by lens-specs from the specs below.</p>
        </section>
      ) : null}

      <section class="dcomplete">
        <div class="dcomplete-bar" aria-hidden="true">
          <div class="dcomplete-fill" style={{ width: `${c.percent ?? 0}%` }} />
        </div>
        <p class="dcomplete-text">
          <strong>
            {c.expected_known} of {c.expected_applicable}
          </strong>{" "}
          key specs cited{c.percent !== null ? ` (${c.percent}%)` : ""}.
          {gaps.length ? (
            <>
              {" "}
              Missing: {gaps.map((g) => g.label).join(", ")}.
            </>
          ) : (
            " Nothing missing."
          )}
        </p>
      </section>

      {pickerOrder(lens.groups, props.registry).map((tier) => (
        <div class="dtier" key={tier.key}>
          {tier.title ? <p class="dtier-title">{tier.title}</p> : null}
          {tier.groups.map(([group, attrs]) => (
            <section class="dgroup" key={`${tier.key}-${group}`}>
              <h2 class="dgroup-title">{titleCase(group)}</h2>
              <div class="drows">
                {attrs.map((a) => (
                  <Row key={a.key} slug={lens.slug} a={a} reg={props.registry.get(a.key)}
                       companion={companionOf(lens.groups, a.key)}
                       registry={props.registry}
                       basis={a.key === lens.category_basis?.attribute && a.state === "known"
                         ? categoryBasisNote(lens.category_basis, lens.mount.name) : null} />
                ))}
              </div>
            </section>
          ))}
        </div>
      ))}

      <p class="dfoot">
        Tap any spec to see where its number came from. A dash means nobody has cited it yet;
        it never means zero. “n/a” means the spec can’t apply to this lens (a prime has
        no separate longest-end aperture).
      </p>
      <LegendNote />
    </main>
  );
}

export { ApiProblem, api };
